import { Switch } from "@headlessui/react";
import { TextInput } from "components/core";
import { FormikProps } from "formik";
import { FC } from "react";
import { classNames } from "utils";

export interface AcceptTermsOfAgreementProps {
  form: FormikProps<any>;
  labelText?: string;
}

const AcceptTermsOfAgreement: FC<AcceptTermsOfAgreementProps> = ({
  form
}) => {
  return (
    <div className="space-y-6"> {/* Removed divide-y for cleaner spacing */}
      {/* Offer Information Section */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-3">
          Terms of Agreement
        </h3>
        <div className="prose">
          <p>Comfy Technology Ltd(CTL) is hereby excluded from any liability arising from the actions of Users of its platform CRBS,placeIT & KAVA in offering a risk to a reinsurer or participating on a risk offered by a reinsurance broker and or insurer.</p>
        </div>
        <div className="col-span-2 p-3 bg-gray-300 rounded-lg mt-6">
          <Switch.Group as="div">
            <span className="flex flex-grow flex-col">
              <Switch.Label
                as="span"
                className="text-sm font-medium text-gray-900"
                passive
              >
                Accept Terms of Agreement
              </Switch.Label>
            </span>
            <div className="flex items-center justify-between h-[38px]">
              <Switch.Description as="span" className="text-sm text-gray-500 mr-6">
                By toggling this switch, you confirm that you have read and agree to the terms of agreement outlined above.
              </Switch.Description>
              <Switch
                checked={form.values.termsAccepted}
                onChange={(val: boolean) => {
                  form.setFieldValue(
                    "termsAccepted",
                    val
                  );

                }}
                className={classNames(
                  form.values.termsAccepted
                    ? "bg-primary-600"
                    : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    form.values.termsAccepted
                      ? "translate-x-5"
                      : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          </Switch.Group>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-3">
          Waiver Liabilty
        </h3>
        <div className="prose">
          <h1>Comfy Technology Ltd. (CTL) - CRBS Waiver of Liability</h1>
          <p><strong>Specific Functionality Focus:</strong> Ability to publish offers to Kava Marketplace</p>
          <p><strong>Effective Date:</strong> June 21, 2025</p>

          <p>By using the feature within the Comfy Reinsurance Brokerage System (CRBS) that allows you to publish offers to the Kava Marketplace, you acknowledge and agree to the following Waiver of Liability. This waiver is an integral part of your agreement to use this specific functionality.</p>

          <h2>1. Acknowledgment of Risk in Publishing Offers</h2>
          <p>You understand that the act of publishing reinsurance facultative and treaty business offers to a marketplace like Kava involves inherent risks, including but not limited to:</p>
          <ul>
            <li>The accuracy and completeness of the information you provide in the offer.</li>
            <li>The potential for misinterpretation or misuse of the published information by third parties on Kava.</li>
            <li>The commercial terms and legal implications of any reinsurance business that may arise from a published offer.</li>
            <li>The inability to control how reinsurers or other parties on Kava react to or process your published offer.</li>
          </ul>

          <h2>2. Exclusion of Liability for Publishing Actions</h2>
          <p>Comfy Technology Ltd. (CTL) is hereby expressly excluded from any and all liability, claims, demands, actions, and causes of action whatsoever, whether in law or in equity, arising from your actions, inactions, omissions, or negligence when publishing business offers via CRBS to the Kava Marketplace, including, but not limited to:</p>
          <ul>
            <li>Any errors, omissions, or inaccuracies in the data or information contained within the offers you publish.</li>
            <li>Any decisions made by third parties on the Kava Marketplace based on the offers you publish.</li>
            <li>Any financial losses, damages, or liabilities incurred by you or any third party as a result of an offer being published, viewed, reviewed, accepted, or otherwise acted upon on the Kava Marketplace.</li>
            <li>Any consequences arising from the content, terms, or nature of the reinsurance business offers you choose to publish.</li>
          </ul>

          <h2>3. No Guarantee of Offer Outcomes</h2>
          <p>CTL makes no representations or warranties regarding the outcome or success of any business offer you publish through CRBS to the Kava Marketplace. The decision to publish an offer, and the subsequent response from any reinsurer or party on Kava, rests solely with you and the Kava users, respectively. CTL is not responsible for the commercial terms, negotiation, or results of any transactions arising from your published offers.</p>

          <h2>4. User Responsibility for Published Content</h2>
          <p>You acknowledge and agree that you are solely responsible for:</p>
          <ul>
            <li>Ensuring the accuracy, completeness, and legality of all data and information within any reinsurance business offer you publish to the Kava Marketplace via CRBS.</li>
            <li>Complying with all applicable laws, regulations, and industry standards related to the content and publication of your reinsurance offers.</li>
            <li>Conducting your own due diligence regarding any potential reinsurer or party on Kava who may respond to your published offer.</li>
            <li>Maintaining the confidentiality and security of your CRBS account credentials related to the publishing feature.</li>
          </ul>

          <h2>5. Indemnification</h2>
          <p>You agree to indemnify, defend, and hold harmless CTL, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the CRBS publishing feature, including, but not limited to, the content of your published offers, your use of this feature's content, services, and products other than as expressly authorized in these Terms, or your use of any information obtained from this feature.</p>

          <p><strong>By proceeding to use the publishing feature within CRBS, you indicate your explicit agreement to this Waiver of Liability.</strong></p>
        </div>
        <div className="col-span-2 p-3 bg-gray-300 rounded-lg mt-6">
          <Switch.Group as="div">
            <span className="flex flex-grow flex-col">
              <Switch.Label
                as="span"
                className="text-sm font-medium text-gray-900"
                passive
              >
                Accept Waiver Liability
              </Switch.Label>
            </span>
            <div className="flex items-center justify-between h-[38px]">
              <Switch.Description as="span" className="text-sm text-gray-500 mr-6">
                By toggling this switch, you confirm that you have read and agree to the waiver  liability outlined above.
              </Switch.Description>
              <Switch
                checked={form.values.waiverAccepted}
                onChange={(val: boolean) => {
                  form.setFieldValue(
                    "waiverAccepted",
                    val
                  );

                }}
                className={classNames(
                  form.values.waiverAccepted
                    ? "bg-primary-600"
                    : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    form.values.waiverAccepted
                      ? "translate-x-5"
                      : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          </Switch.Group>
        </div>
      </div>
    </div>
  );
};

export default AcceptTermsOfAgreement;
